import axios from "axios";
import {TRACK_DOCUSEAL_SUBMISSION_URL, get_request_config} from "@/utils/system_constants/constant";
import { parse_response_data } from "../EsigningSetup/esigning_setup_helpers";
import {
    build_tracker_document,
    parse_positive_number,
    update_summary_count
} from "./signing_tracker_display_helpers";
import {
    get_tracking_error_message,
    handle_tracking_response,
    set_tracking_error
} from "./signing_tracker_service_helpers";

function show_system_loader(message) {
    window.dispatchEvent(new CustomEvent("system-loader:show", {
        detail: message
    }));
}

function hide_system_loader_after_render(instance) {
    instance.$nextTick(() => {
        window.dispatchEvent(new CustomEvent("system-loader:hide"));
    });
}

export default {
    name: "SigningTracker",
    data: () => ({
        open_recipient_ids: [],
        tracking_loading: false,
        tracking_error: "",
        tracking_response: null
    }),
    computed: {
        submission_id() {
            return parse_positive_number(this.$route?.query?.submissionId);
        },
        route_entity_profile_id() {
            return parse_positive_number(this.$route?.query?.entity_profile_id || this.$route?.query?.entityProfileId);
        },
        route_entity_type_id() {
            return parse_positive_number(this.$route?.query?.entity_type_id || this.$route?.query?.entityTypeId);
        },
        route_application_id() {
            return parse_positive_number(this.$route?.query?.application_id || this.$route?.query?.applicationId);
        },
        tracker_document() {
            return build_tracker_document(this.$route?.query, this.tracking_response);
        },
        recipients() {
            return this.tracker_document.recipients || [];
        },
        has_recipients() {
            return this.recipients.length > 0;
        },
        summary_counts() {
            return this.recipients.reduce((summary, recipient) => {
                const status = String(recipient.status_key || "").toLowerCase();
                return update_summary_count(summary, status);
            }, { signed: 0, viewed: 0, pending: 0 });
        },
        overall_completion() {
            const recipient_count = this.recipients.length || 1;
            return Math.round((this.summary_counts.signed / recipient_count) * 100);
        },
        overall_completion_width() {
            return `${this.overall_completion}%`;
        }
    },
    mounted() {
        this.load_tracking();
    },
    watch: {
        recipients() {
            this.reset_open_recipients();
        },
        "$route.query.submissionId"() {
            this.load_tracking();
        }
    },
    methods: {
        parse_response_data,
        reset_open_recipients() {
            const first_recipient_id = this.recipients?.[0]?.id;
            this.open_recipient_ids = first_recipient_id ? [first_recipient_id] : [];
        },
        is_recipient_open(recipient_id) {
            return this.open_recipient_ids.includes(recipient_id);
        },
        toggle_recipient(recipient_id) {
            if (this.is_recipient_open(recipient_id)) {
                this.open_recipient_ids = this.open_recipient_ids.filter((id) => id !== recipient_id);
                return;
            }

            this.open_recipient_ids = [...this.open_recipient_ids, recipient_id];
        },
        async load_tracking() {
            this.reset_open_recipients();
            this.tracking_error = "";
            this.tracking_response = null;

            if (!this.submission_id) {
                set_tracking_error(this, "No submission was provided for tracking.");
                return;
            }

            this.tracking_loading = true;
            show_system_loader("Loading signing tracker...");

            try {
                const response = await axios.get(
                    TRACK_DOCUSEAL_SUBMISSION_URL,
                    get_request_config({
                        params: {
                            submission_id: this.submission_id,
                            ...(this.route_entity_profile_id ? { entity_profile_id: this.route_entity_profile_id } : {})
                        }
                    })
                );
                const parsed_response = this.parse_response_data(response.data);
                handle_tracking_response(this, parsed_response);
            } catch (error) {
                set_tracking_error(this, get_tracking_error_message(this, error));
            } finally {
                this.tracking_loading = false;
                hide_system_loader_after_render(this);
            }
        },
        open_sign_link(recipient) {
            if (!recipient?.sign_url || typeof window === "undefined") {
                return;
            }

            window.open(recipient.sign_url, "_blank", "noopener,noreferrer");
        },
        go_back_to_esigning() {
            const query = {};
            if (this.route_entity_profile_id) {
                query.entity_profile_id = String(this.route_entity_profile_id);
            }
            if (this.route_entity_type_id) {
                query.entity_type_id = String(this.route_entity_type_id);
            }
            if (this.route_application_id) {
                query.application_id = String(this.route_application_id);
            }

            this.$router.push({
                name: this.route_application_id ? "OnboardingEsigningCase" : "OnboardingEsigningSetup",
                ...(this.route_application_id ? {
                    params: {
                        applicationId: String(this.route_application_id)
                    }
                } : {}),
                ...(Object.keys(query).length ? { query } : {})
            });
        }
    }
};
